import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useAutoSave } from '../hooks/useAutoSave';
import { providerAPI, providerTypeAPI, payeeAPI, credentialCodeAPI, serviceAreaAPI, lookupAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import EyeIcon from './icons/EyeIcon';
import DeleteIconButton from './DeleteIconButton';
import SearchableProviderSelect from './SearchableProviderSelect';
import './ProviderForm.css';

const emptySite = {
  street_1: '',
  street_2: '',
  city: '',
  state: 'IL',
  zip: '',
  phone: '',
  fax: '',
  label: '',
};

const EXCLUDED_CREDENTIAL_CODES = ['PECS', 'VaccinatedCovid19', 'VaccinatedCovid-19', 'CoachingModel', 'Evening', 'Weekend', 'WillingToDoInHome'];

function isExcludedCredential(cred) {
  const code = (cred.code || '').trim();
  const name = (cred.name || '').toLowerCase();
  if (EXCLUDED_CREDENTIAL_CODES.includes(code)) return true;
  if (/pecs/i.test(code) || /pecs/i.test(name)) return true;
  if (/vaccinated.*covid|vaccinatedcovid/i.test(code) || /vaccinated.*covid/i.test(name)) return true;
  if (/coaching\s*model|coachingmodel/i.test(code) || /coaching\s*model/i.test(name)) return true;
  return false;
}

function ProviderForm() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const pathname = location.pathname || '';
  /** Same pattern as ContactForm / SchoolForm: /client/:id/provider/:providerId keeps ClientLayout + main nav. */
  const isNestedUnderClient = pathname.includes('/client/') && pathname.includes('/provider/');
  const providerId = isNestedUnderClient ? params.providerId : params.id;
  const clientIdParam = isNestedUnderClient ? params.id : null;
  const returnToPath = location.state?.returnToPath ?? null;
  const returnToClientId = location.state?.returnToClientId ?? null;
  const backToClientId = clientIdParam ?? returnToClientId;
  const backToCancel = clientIdParam ? tenantPath(`/client/${clientIdParam}`) : tenantPath('/lists/providers');
  const isEdit = !!providerId && providerId !== 'new';

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    middle_name: '',
    phone: '',
    email: '',
    provider_type_id: '',
    payee_id: '',
    NPI: '',
    license: '',
    coaching_model: '',
    comment: '',
    evening_available: false,
    weekend_available: false,
    willing_in_home: false,
    supervisor_id: null,
  });
  const [primarySite, setPrimarySite] = useState({ ...emptySite });
  const [additionalSites, setAdditionalSites] = useState([]);
  const [credentialIds, setCredentialIds] = useState(new Set());
  const [languageIds, setLanguageIds] = useState(new Set());
  const [serviceAreaIds, setServiceAreaIds] = useState(new Set());

  const [providerTypes, setProviderTypes] = useState([]);
  const [credentialCodes, setCredentialCodes] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  /** Payee row: email + addresses (read-only) for context */
  const [payeeEmailDisplay, setPayeeEmailDisplay] = useState('');
  const [payeeEmailLoading, setPayeeEmailLoading] = useState(false);
  /** Ignore stale payee getById responses when payee_id or provider load changes quickly. */
  const payeeEmailFetchSeqRef = useRef(0);
  const [isActive, setIsActive] = useState(true);
  const [togglingActive, setTogglingActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [saveTrigger, setSaveTrigger] = useState(0);
  const [payeeFieldInfoOpen, setPayeeFieldInfoOpen] = useState(false);
  /** Payee pick list (billing entity only; offices are managed in Offices below). */
  const [payeesOptions, setPayeesOptions] = useState([]);
  /** Payee offices (for linking an extra practice location without editing the payee). */
  const [payeeAddressesForLink, setPayeeAddressesForLink] = useState([]);
  const [linkedPracticeSiteIds, setLinkedPracticeSiteIds] = useState([]);
  const [addPracticeSiteBusy, setAddPracticeSiteBusy] = useState(false);
  const [practiceOfficesInfoOpen, setPracticeOfficesInfoOpen] = useState(false);
  const [addOfficeFromPayeeInfoOpen, setAddOfficeFromPayeeInfoOpen] = useState(false);

  const applySitesFromProvider = useCallback((provider) => {
    const sitesList = provider.sites || [];
    setLinkedPracticeSiteIds(
      sitesList.map((s) => s.site_id).filter((x) => x != null && x !== '')
    );
    const site0 = sitesList[0];
    setPrimarySite(
      site0
        ? {
            street_1: site0.street_1 || '',
            street_2: site0.street_2 || '',
            city: site0.city || '',
            state: site0.state || '',
            zip: site0.zip || '',
            phone: site0.phone || '',
            fax: site0.fax || '',
            label: '',
          }
        : { ...emptySite }
    );
    setAdditionalSites(
      sitesList.slice(1).map((s) => ({
        site_id: s.site_id ?? null,
        street_1: s.street_1 || '',
        street_2: s.street_2 || '',
        city: s.city || '',
        state: s.state || '',
        zip: s.zip || '',
        phone: s.phone || '',
        fax: s.fax || '',
        label: '',
      }))
    );
  }, []);

  useEffect(() => {
    loadOptions();
    if (isEdit) {
      loadProvider();
    }
  }, [providerId, isEdit]);

  // After creating a payee from "Add payee", select it on this form (same pattern as client → pediatrician).
  useEffect(() => {
    const selId = location.state?.selectPayeeId;
    const selName = location.state?.selectPayeeName;
    if (selId == null || selId === '') return;
    const idStr = String(selId);
    setFormData((prev) => ({ ...prev, payee_id: idStr }));
    if (isEdit) setSaveTrigger((p) => p + 1);
    navigate(`${location.pathname}${location.search || ''}`, { replace: true, state: {} });
  }, [location.state?.selectPayeeId, location.pathname, location.search, navigate, isEdit]);

  // When payee selection changes, load payee email for display next to the Payee field.
  useEffect(() => {
    const payeeId = formData.payee_id ? String(formData.payee_id).trim() : '';
    if (!payeeId) {
      setPayeeEmailDisplay('');
      setPayeeEmailLoading(false);
      return;
    }
    const seq = ++payeeEmailFetchSeqRef.current;
    setPayeeEmailLoading(true);
    payeeAPI.getById(payeeId)
      .then((res) => {
        if (seq !== payeeEmailFetchSeqRef.current) return;
        const p = res.data?.data;
        const email = p?.email;
        setPayeeEmailDisplay(typeof email === 'string' ? email.trim() : '');
      })
      .catch(() => {
        if (seq !== payeeEmailFetchSeqRef.current) return;
        setPayeeEmailDisplay('');
      })
      .finally(() => {
        if (seq === payeeEmailFetchSeqRef.current) setPayeeEmailLoading(false);
      });
  }, [formData.payee_id]);

  useEffect(() => {
    if (!isEdit || !formData.payee_id) {
      setPayeeAddressesForLink([]);
      return;
    }
    let cancelled = false;
    payeeAPI
      .getById(String(formData.payee_id).trim())
      .then((res) => {
        if (cancelled) return;
        setPayeeAddressesForLink(res.data?.data?.addresses ?? []);
      })
      .catch(() => {
        if (!cancelled) setPayeeAddressesForLink([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, formData.payee_id]);

  const practiceLocationsAvailableFromPayee = useMemo(
    () =>
      payeeAddressesForLink.filter((a) => {
        const sid = a.site_id;
        if (sid == null || sid === '') return false;
        return !linkedPracticeSiteIds.includes(Number(sid));
      }),
    [payeeAddressesForLink, linkedPracticeSiteIds]
  );

  const loadOptions = async () => {
    try {
      setLoadingOptions(true);
      const [typesResponse, credsResponse, langsResponse, areasResponse, payeesResponse] = await Promise.all([
        providerTypeAPI.getAll(),
        credentialCodeAPI.getAll().catch(() => ({ data: { data: [] } })),
        lookupAPI.getLanguages().catch(() => ({ data: { data: [] } })),
        serviceAreaAPI.getAll().catch(() => ({ data: { data: [] } })),
        (async () => {
          const merged = [];
          let page = 1;
          const pageSize = 100;
          for (;;) {
            const res = await payeeAPI.getAll({ page, pageSize }).catch(() => ({ data: { data: [] } }));
            const rows = res.data?.data ?? [];
            merged.push(...rows);
            if (rows.length < pageSize) break;
            page += 1;
            if (page > 40) break;
          }
          return merged;
        })(),
      ]);
      setProviderTypes(typesResponse.data.data);
      setCredentialCodes(credsResponse.data?.data ?? []);
      setLanguages(langsResponse.data?.data ?? langsResponse.data ?? []);
      setServiceAreas(areasResponse.data?.data ?? areasResponse.data ?? []);
      const sortedPayees = [...payeesResponse].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
      );
      setPayeesOptions(sortedPayees);
    } catch (error) {
      console.error('Error loading options:', error);
      alert('Failed to load form options');
    } finally {
      setLoadingOptions(false);
    }
  };

  const loadProvider = async () => {
    try {
      setLoading(true);
      const response = await providerAPI.getById(providerId);
      const provider = response.data.data;
      const payeeId = provider.payee_id ?? provider.payees?.[0]?.payee_id ?? null;
      setFormData({
        first_name: provider.first_name,
        last_name: provider.last_name,
        middle_name: provider.middle_name || '',
        phone: provider.phone || '',
        email: provider.email,
        provider_type_id: provider.provider_type_id,
        payee_id: payeeId ?? '',
        NPI: provider.NPI || '',
        license: provider.license || '',
        coaching_model: provider.coaching_model || '',
        comment: provider.comment || '',
        evening_available: provider.evening_available || false,
        weekend_available: provider.weekend_available || false,
        willing_in_home: provider.willing_in_home || false,
        supervisor_id: provider.supervisor_id || null,
      });
      setIsActive(provider.is_active !== false);
      const pe = typeof provider.payee_email === 'string' ? provider.payee_email.trim() : '';
      setPayeeEmailDisplay(pe);
      applySitesFromProvider(provider);
      const credIds = new Set(
        (provider.credentials || [])
          .filter((c) => !['Evening', 'Weekend', 'WillingToDoInHome'].includes(c.credential_code))
          .map((c) => c.credential_code_id ?? c.id)
      );
      setCredentialIds(credIds);
      const langIds = new Set((provider.languages || []).map((l) => l.language_id ?? l.id));
      setLanguageIds(langIds);
      const areaIds = new Set((provider.service_areas || []).map((a) => a.service_area_id ?? a.id));
      setServiceAreaIds(areaIds);
    } catch (error) {
      console.error('Error loading provider:', error);
      alert('Failed to load provider');
      navigate(clientIdParam ? tenantPath(`/client/${clientIdParam}`) : tenantPath('/lists/providers'));
    } finally {
      setLoading(false);
    }
  };

  const triggerSave = () => {
    if (isEdit) setSaveTrigger((p) => p + 1);
  };

  const handleToggleActive = async () => {
    if (!providerId) return;
    const confirmMsg = isActive
      ? 'Deactivate this provider? They will be hidden from search and dropdowns but their records will be preserved.'
      : 'Reactivate this provider?';
    if (!window.confirm(confirmMsg)) return;
    setTogglingActive(true);
    try {
      if (isActive) {
        const res = await providerAPI.delete(providerId);
        setIsActive(res.data.action !== 'deactivated' ? true : false);
        if (res.data.action === 'deleted') {
          navigate(backToCancel);
          return;
        }
      } else {
        await providerAPI.reactivate(providerId);
        setIsActive(true);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update provider status');
    } finally {
      setTogglingActive(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    triggerSave();
  };

  const hasSiteContent = (s) =>
    !!(s.street_1 || s.street_2 || s.city || s.zip || s.phone || s.fax);

  /** One-line display: Office N + street / city / state / phone / fax. */
  const formatOfficeRowLine = (s, officeNumber) => {
    const street = [s.street_1, s.street_2].filter(Boolean).join(', ');
    const locality = [s.city, s.state, s.zip].filter(Boolean).join(' ');
    const address = [locality, street].filter(Boolean).join(' · ');
    const phone = s.phone ? `Phone: ${String(s.phone).trim()}` : '';
    const fax = s.fax ? `Fax: ${String(s.fax).trim()}` : '';
    const main = [address, phone, fax].filter(Boolean).join(' · ');
    const prefix = officeNumber != null ? `Office ${officeNumber} — ` : '';
    if (main) return `${prefix}${main}`;
    return officeNumber != null ? `${prefix}(no address on file)` : '(no address on file)';
  };

  const providerLocationsOnFile = () => {
    const out = [];
    if (hasSiteContent(primarySite)) out.push(primarySite);
    additionalSites.filter(hasSiteContent).forEach((x) => out.push(x));
    return out;
  };

  const addCredential = (credentialCodeId) => {
    if (!credentialCodeId) return;
    setCredentialIds((prev) => new Set([...prev, parseInt(credentialCodeId, 10)]));
    triggerSave();
  };
  const removeCredential = (credentialCodeId) => {
    setCredentialIds((prev) => {
      const next = new Set(prev);
      next.delete(credentialCodeId);
      return next;
    });
    triggerSave();
  };
  const addLanguage = (languageId) => {
    if (!languageId) return;
    setLanguageIds((prev) => new Set([...prev, parseInt(languageId, 10)]));
    triggerSave();
  };
  const removeLanguage = (languageId) => {
    setLanguageIds((prev) => {
      const next = new Set(prev);
      next.delete(languageId);
      return next;
    });
    triggerSave();
  };
  const addServiceArea = (serviceAreaId) => {
    if (!serviceAreaId) return;
    setServiceAreaIds((prev) => new Set([...prev, parseInt(serviceAreaId, 10)]));
    triggerSave();
  };
  const removeServiceArea = (serviceAreaId) => {
    setServiceAreaIds((prev) => {
      const next = new Set(prev);
      next.delete(serviceAreaId);
      return next;
    });
    triggerSave();
  };

  const removeOffice = (isPrimary, additionalIdx) => {
    if (isPrimary) {
      setPrimarySite({ ...emptySite });
    } else {
      setAdditionalSites((prev) => {
        const removed = prev[additionalIdx];
        if (removed?.site_id != null) {
          setLinkedPracticeSiteIds((ids) => ids.filter((id) => id !== removed.site_id));
        }
        return prev.filter((_, i) => i !== additionalIdx);
      });
    }
    triggerSave();
  };

  const handleAddPracticeSiteFromPayee = async (siteIdStr) => {
    if (!siteIdStr || !isEdit || !providerId) return;
    setAddPracticeSiteBusy(true);
    try {
      const res = await providerAPI.addPracticeSite(providerId, { site_id: parseInt(siteIdStr, 10) });
      applySitesFromProvider(res.data.data);
      setSaveTrigger((p) => p + 1);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Failed to add location';
      alert(msg);
    } finally {
      setAddPracticeSiteBusy(false);
    }
  };

  const performSave = useCallback(async () => {
    const allSites = [
      {
        ...primarySite,
        phone: primarySite.phone || formData.phone,
      },
      ...additionalSites.map((s) => ({ ...s })),
    ].filter(hasSiteContent);
    let sites = allSites;
    if (sites.length === 0 && (formData.phone || formData.email)) {
      sites = [{
        ...emptySite,
        phone: formData.phone || '',
        label: '',
      }];
    }
    const payeeId = formData.payee_id ? String(formData.payee_id).trim() || null : null;
    const payees = payeeId ? [{ payee_id: payeeId }] : [];
    const credentials = Array.from(credentialIds).map((credential_code_id) => ({ credential_code_id }));
    const languagesPayload = Array.from(languageIds).map((language_id) => ({ language_id }));
    const service_areas = Array.from(serviceAreaIds).map((service_area_id) => ({ service_area_id }));
    const evaluatorCode = credentialCodes.find((c) => c.code === 'Evaluator');
    const assistantCode = credentialCodes.find((c) => c.code === 'Assistant');
    const evaluator = evaluatorCode ? credentialIds.has(evaluatorCode.id) : false;
    const assistant = assistantCode ? credentialIds.has(assistantCode.id) : false;

    const submitData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      middle_name: formData.middle_name || null,
      phone: formData.phone || null,
      email: formData.email,
      evaluator,
      assistant,
      provider_type_id: parseInt(formData.provider_type_id, 10),
      payee_id: payeeId,
      NPI: formData.NPI || null,
      license: formData.license || null,
      coaching_model: formData.coaching_model?.trim() || null,
      comment: formData.comment?.trim() || null,
      evening_available: formData.evening_available,
      weekend_available: formData.weekend_available,
      willing_in_home: formData.willing_in_home,
      supervisor_id: formData.supervisor_id || null,
      sites,
      payees,
      credentials,
      languages: languagesPayload,
      service_areas,
    };

    if (isEdit) {
      await providerAPI.update(providerId, submitData);
    } else {
      await providerAPI.create(submitData);
    }
  }, [
    formData, primarySite, additionalSites, credentialIds, languageIds, serviceAreaIds,
    credentialCodes, isEdit, providerId,
  ]);

  useAutoSave(saveTrigger, performSave, { enabled: isEdit, delayMs: 500 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performSave();
      if (returnToPath) {
        navigate(returnToPath);
      } else if (backToClientId) {
        navigate(tenantPath(`/client/${backToClientId}`));
      } else {
        navigate(tenantPath('/lists/providers'));
      }
    } catch (error) {
      console.error('Error saving provider:', error);
      const message = error.response?.data?.error || error.message || 'Failed to save provider';
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) {
    return <div className="loading">Loading form...</div>;
  }

  if (loading && isEdit) {
    return <div className="loading">Loading provider...</div>;
  }

  return (
    <div className="provider-form">
      <div className="provider-form-header">
        <div className="provider-form-title-row">
          <h1>{isEdit ? 'Edit Provider' : 'Add New Provider'}</h1>
          {isEdit && (
            <span className={`provider-active-badge${isActive ? '' : ' provider-active-badge--inactive'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          )}
        </div>
        <div className="provider-form-header-actions">
          {isEdit && hasPermission('providers.update') && (
            <button
              type="button"
              className={`provider-toggle-active-btn${isActive ? ' provider-toggle-active-btn--deactivate' : ' provider-toggle-active-btn--reactivate'}`}
              onClick={handleToggleActive}
              disabled={togglingActive}
            >
              {togglingActive ? '...' : isActive ? 'Deactivate' : 'Reactivate'}
            </button>
          )}
          {!isEdit && (
            <>
              <Link to={backToCancel} className="btn-cancel">Cancel</Link>
              <button type="submit" form="provider-form-id" disabled={loading} className="btn-save">
                {loading ? 'Saving...' : 'Create Provider'}
              </button>
            </>
          )}
        </div>
      </div>

      <form id="provider-form-id" onSubmit={handleSubmit}>
        <section className="form-section">
          <h2>Name &amp; type</h2>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Middle Name</label>
              <input type="text" name="middle_name" value={formData.middle_name} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Provider Type *</label>
              <select name="provider_type_id" value={formData.provider_type_id} onChange={handleChange} required>
                <option value="">Select a type...</option>
                {providerTypes.map((type) => (
                  <option key={type.id} value={type.id}>{type.code} - {type.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>License</label>
              <input type="text" name="license" value={formData.license} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>NPI</label>
              <input type="text" name="NPI" value={formData.NPI} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group form-group-payee-with-info form-group-span-full">
              <div className="payee-label-row">
                <label htmlFor="provider-form-payee-select">Payee *</label>
                <button
                  type="button"
                  className="payee-field-info-btn"
                  aria-expanded={payeeFieldInfoOpen}
                  aria-controls="provider-form-payee-info-panel"
                  onClick={() => setPayeeFieldInfoOpen((o) => !o)}
                  title="About payee and billing offices"
                >
                  <span className="payee-field-info-icon" aria-hidden="true">i</span>
                  <span className="sr-only">About payee and billing offices</span>
                </button>
                {hasPermission('payees.create') && (
                  <button
                    type="button"
                    className="btn-new-payee-icon"
                    onClick={() => navigate(tenantPath('/payee/new'), {
                      state: { returnToPath: `${location.pathname}${location.search || ''}` },
                    })}
                    title="Create a new payee, then return here to select them for this provider"
                    aria-label="Add new payee"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                )}
              </div>
              {payeeFieldInfoOpen && (
                <div id="provider-form-payee-info-panel" className="payee-field-info-panel" role="region" aria-label="Payee field help">
                  <p>
                    Select the <strong>billing payee</strong> (organization) for this provider. This is only the payee identity—offices (locations) are managed in the <strong>Offices</strong> section below and on the payee record.
                  </p>
                </div>
              )}
              <div className="payee-select-row-with-email">
                <div className="payee-select-primary">
                  <select
                    id="provider-form-payee-select"
                    name="payee_id"
                    className="provider-form-payee-select"
                    value={formData.payee_id ? String(formData.payee_id) : ''}
                    onChange={handleChange}
                    required
                    aria-label="Payee"
                  >
                    <option value="">Select payee…</option>
                    {formData.payee_id &&
                      !payeesOptions.some((p) => String(p.id) === String(formData.payee_id).trim()) && (
                        <option value={String(formData.payee_id).trim()}>Current payee (not in list—save or refresh)</option>
                      )}
                    {payeesOptions.map((py) => (
                      <option key={py.id} value={String(py.id)}>
                        {py.name || py.id}
                      </option>
                    ))}
                  </select>
                  {formData.payee_id ? (
                    <button
                      type="button"
                      className="payee-view-btn"
                      title="View payee record"
                      aria-label="View payee record"
                      onClick={() => navigate(tenantPath(`/lists/payees/${String(formData.payee_id).trim()}`))}
                    >
                      <EyeIcon size={18} title="" />
                    </button>
                  ) : null}
                </div>
                {formData.payee_id ? (
                  <div className="payee-email-inline">
                    <label htmlFor="provider-form-payee-email" className="payee-email-field-label">
                      Payee email
                    </label>
                    <input
                      id="provider-form-payee-email"
                      type="text"
                      readOnly
                      tabIndex={-1}
                      className="payee-email-input-readonly"
                      value={payeeEmailLoading ? 'Loading…' : (payeeEmailDisplay || '—')}
                      title={payeeEmailLoading ? 'Loading payee email…' : (payeeEmailDisplay || 'No email on payee record')}
                      aria-label="Payee email from payee record"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        <section className="form-section">
          <h2>Contact (provider)</h2>
          <div className="form-row">
            <div className="form-group">
              <label>Provider email</label>
              <div className="provider-email-input-row">
                <input type="email" name="email" value={formData.email} onChange={handleChange} />
                {formData.email && (
                  <a
                    href={`mailto:${formData.email}`}
                    className="provider-email-mailto-btn"
                    title={`Send email to ${formData.email}`}
                    aria-label={`Open email client to send to ${formData.email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={0}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <polyline points="2,4 12,13 22,4" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Provider phone</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} />
            </div>
          </div>
        </section>

        <section className="form-section provider-form-offices-section" aria-labelledby="provider-offices-heading">
          <div className="provider-form-section-heading-row">
            <h2 id="provider-offices-heading">Offices</h2>
            <button
              type="button"
              className="payee-field-info-btn"
              aria-expanded={practiceOfficesInfoOpen}
              aria-controls="provider-offices-info"
              onClick={() => setPracticeOfficesInfoOpen((o) => !o)}
              title="About offices"
            >
              <span className="payee-field-info-icon" aria-hidden="true">i</span>
              <span className="sr-only">About offices</span>
            </button>
          </div>
          {practiceOfficesInfoOpen && (
            <div id="provider-offices-info" className="payee-field-info-panel" role="region" aria-label="Offices help">
              <p>
                On the <strong>payee</strong> record, locations are <strong>Office 1</strong>, <strong>Office 2</strong>, etc. (street, phone, fax, and so on). Here you only choose <strong>which of those offices this provider works at</strong>—you are not entering new location details.
              </p>
              <p>
                To add a <strong>new</strong> office for the practice, edit the payee and use <strong>Add Office</strong> there, then return here and use <strong>Add office from payee</strong>.
              </p>
              <p>
                Client pediatrician and referral pickers show offices linked on this provider, not every office on the payee.
              </p>
            </div>
          )}

          {isEdit ? (
            (() => {
              const hasPrimary = hasSiteContent(primarySite);
              const validAdditional = additionalSites.map((s, i) => ({ s, i })).filter(({ s }) => hasSiteContent(s));
              const allOffices = [
                ...(hasPrimary ? [{ s: primarySite, isPrimary: true, additionalIdx: null }] : []),
                ...validAdditional.map(({ s, i }) => ({ s, isPrimary: false, additionalIdx: i })),
              ];
              return allOffices.length > 0 ? (
                <ul className="provider-form-office-list">
                  {allOffices.map((item, displayIdx) => (
                    <li key={displayIdx} className="provider-form-office-list-item">
                      <span>{formatOfficeRowLine(item.s, displayIdx + 1)}</span>
                      {hasPermission('providers.update') && (
                        <DeleteIconButton
                          onClick={() => removeOffice(item.isPrimary, item.additionalIdx)}
                          aria-label={`Remove Office ${displayIdx + 1}`}
                          title={`Remove Office ${displayIdx + 1}`}
                          className="provider-form-office-remove"
                        />
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="provider-form-office-empty">No offices linked yet.</p>
              );
            })()
          ) : (
            <p className="form-hint provider-form-office-new-hint">Save this provider first, then you can link offices from the payee.</p>
          )}

          {isEdit && formData.payee_id && hasPermission('providers.update') && (
            <div className="provider-form-add-office-from-payee">
              <div className="provider-form-section-heading-row provider-form-subheading-row">
                <label className="provider-form-inline-section-label" htmlFor="provider-add-office-from-payee-select">
                  Add office from payee
                </label>
                <button
                  type="button"
                  className="payee-field-info-btn"
                  aria-expanded={addOfficeFromPayeeInfoOpen}
                  aria-controls="provider-add-office-from-payee-info"
                  onClick={() => setAddOfficeFromPayeeInfoOpen((o) => !o)}
                  title="About adding an office from the payee"
                >
                  <span className="payee-field-info-icon" aria-hidden="true">i</span>
                  <span className="sr-only">About adding an office from the payee</span>
                </button>
              </div>
              {addOfficeFromPayeeInfoOpen && (
                <div id="provider-add-office-from-payee-info" className="payee-field-info-panel" role="region" aria-label="Add office from payee help">
                  <p>
                    The dropdown lists payee offices that are <strong>not</strong> already linked to this provider. Choosing one records that this provider works there (for pickers and assignments). It does <strong>not</strong> create a new address.
                  </p>
                </div>
              )}
              <select
                key={linkedPracticeSiteIds.join('-') || 'none'}
                id="provider-add-office-from-payee-select"
                disabled={addPracticeSiteBusy || practiceLocationsAvailableFromPayee.length === 0}
                defaultValue=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  handleAddPracticeSiteFromPayee(v);
                }}
                aria-label="Add office from payee"
              >
                <option value="">
                  {addPracticeSiteBusy ? 'Adding…' : 'Select office to add…'}
                </option>
                {practiceLocationsAvailableFromPayee.map((a) => {
                  const payeeIdx = payeeAddressesForLink.findIndex((x) => String(x.site_id) === String(a.site_id));
                  const officeNum = payeeIdx >= 0 ? payeeIdx + 1 : null;
                  return (
                    <option key={a.site_id} value={String(a.site_id)}>
                      {formatOfficeRowLine(a, officeNum)}
                    </option>
                  );
                })}
              </select>
              {payeeAddressesForLink.length === 0 && (
                <p className="form-hint provider-form-office-status-hint">No offices on this payee yet. Add them on the payee record.</p>
              )}
              {payeeAddressesForLink.some((a) => a.site_id != null && a.site_id !== '') &&
                practiceLocationsAvailableFromPayee.length === 0 &&
                payeeAddressesForLink.length > 0 && (
                  <p className="form-hint provider-form-office-status-hint">All payee offices are already linked.</p>
                )}
              {payeeAddressesForLink.length > 0 && payeeAddressesForLink.every((a) => a.site_id == null || a.site_id === '') && (
                <p className="form-hint provider-form-office-status-hint">
                  This payee’s rows are not on the shared office model yet. Contact support if you need help migrating.
                </p>
              )}
            </div>
          )}
        </section>

        <section className="form-section">
          <h2>Willing to work</h2>
          <p className="form-hint">When this provider is willing to work.</p>
          <div className="form-row form-row-inline">
            <label className="checkbox-label">
              <input type="checkbox" name="evening_available" checked={formData.evening_available} onChange={handleChange} />
              Evening
            </label>
            <label className="checkbox-label">
              <input type="checkbox" name="weekend_available" checked={formData.weekend_available} onChange={handleChange} />
              Weekend
            </label>
            <label className="checkbox-label">
              <input type="checkbox" name="willing_in_home" checked={formData.willing_in_home} onChange={handleChange} />
              Willing to do in-home
            </label>
          </div>
        </section>

        <section className="form-section form-section-three-col">
          <h2>Credentials, languages &amp; service areas</h2>
          <div className="three-col-box">
            <div className="multi-select-col">
              <h3>Credentials</h3>
              <p className="form-hint">Add credentials (e.g. BCBA, Evaluator, Assistant).</p>
              <div className="multi-select-group">
                <select
                  value=""
                  onChange={(e) => { addCredential(e.target.value); e.target.value = ''; }}
                  className="multi-select-dropdown"
                  aria-label="Add credential"
                >
                  <option value="">Add a credential...</option>
                  {credentialCodes
                    .filter((c) => !isExcludedCredential(c))
                    .filter((c) => !credentialIds.has(c.id))
                    .map((cred) => (
                      <option key={cred.id} value={cred.id}>{cred.name || cred.code}</option>
                    ))}
                </select>
                <div className="multi-select-chips">
                  {credentialCodes
                    .filter((c) => !isExcludedCredential(c))
                    .filter((c) => credentialIds.has(c.id))
                    .map((cred) => (
                      <span key={cred.id} className="multi-select-chip">
                        {cred.name || cred.code}
                        <button type="button" onClick={() => removeCredential(cred.id)} className="multi-select-chip-remove" aria-label={`Remove ${cred.name || cred.code}`}>&times;</button>
                      </span>
                    ))}
                </div>
              </div>
            </div>
            <div className="multi-select-col">
              <h3>Languages</h3>
              <p className="form-hint">Languages this provider can use (zero or more).</p>
              <div className="multi-select-group">
                <select
                  value=""
                  onChange={(e) => { addLanguage(e.target.value); e.target.value = ''; }}
                  className="multi-select-dropdown"
                  aria-label="Add language"
                >
                  <option value="">Add a language...</option>
                  {(languages || [])
                    .filter((l) => !languageIds.has(l.id))
                    .map((l) => (
                      <option key={l.id} value={l.id}>{l.description || l.code}</option>
                    ))}
                </select>
                <div className="multi-select-chips">
                  {(languages || [])
                    .filter((l) => languageIds.has(l.id))
                    .map((l) => (
                      <span key={l.id} className="multi-select-chip">
                        {l.description || l.code}
                        <button type="button" onClick={() => removeLanguage(l.id)} className="multi-select-chip-remove" aria-label={`Remove ${l.description || l.code}`}>&times;</button>
                      </span>
                    ))}
                </div>
              </div>
            </div>
            <div className="multi-select-col">
              <h3>Service areas</h3>
              <p className="form-hint">Geographic or program areas this provider serves (zero or more).</p>
              <div className="multi-select-group">
                <select
                  value=""
                  onChange={(e) => { addServiceArea(e.target.value); e.target.value = ''; }}
                  className="multi-select-dropdown"
                  aria-label="Add service area"
                >
                  <option value="">Add a service area...</option>
                  {(serviceAreas || [])
                    .filter((a) => !serviceAreaIds.has(a.id))
                    .map((a) => (
                      <option key={a.id} value={a.id}>{a.name || a.code}</option>
                    ))}
                </select>
                <div className="multi-select-chips">
                  {(serviceAreas || [])
                    .filter((a) => serviceAreaIds.has(a.id))
                    .map((a) => (
                      <span key={a.id} className="multi-select-chip">
                        {a.name || a.code}
                        <button type="button" onClick={() => removeServiceArea(a.id)} className="multi-select-chip-remove" aria-label={`Remove ${a.name || a.code}`}>&times;</button>
                      </span>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {(() => {
          const assistantCode = credentialCodes.find((c) => c.code === 'Assistant');
          const isAssistant = assistantCode ? credentialIds.has(assistantCode.id) : false;
          if (!isAssistant) return null;
          const selectedType = providerTypes.find((t) => String(t.id) === String(formData.provider_type_id));
          const supervisorTypeCode = selectedType?.code || undefined;
          return (
            <section className="form-section">
              <h2>Supervisor</h2>
              <p className="form-hint">
                The supervising provider for this assistant
                {selectedType ? ` (${selectedType.name} only)` : ''}.
              </p>
              <div className="form-group">
                <SearchableProviderSelect
                  value={formData.supervisor_id || ''}
                  onChange={(val) => setFormData((prev) => ({ ...prev, supervisor_id: val || null }))}
                  placeholder="Search by name..."
                  typeCode={supervisorTypeCode}
                  id="supervisor-select"
                />
              </div>
            </section>
          );
        })()}

        <section className="form-section">
          <h2>Coaching model</h2>
          <p className="form-hint">How they approach their job / how they do their work (optional).</p>
          <div className="form-group">
            <textarea
              name="coaching_model"
              value={formData.coaching_model}
              onChange={handleChange}
              rows={4}
              placeholder="Describe the provider's coaching approach..."
              className="coaching-model-textarea"
            />
          </div>
        </section>

        <section className="form-section">
          <h2>Comment</h2>
          <p className="form-hint">General notes about this provider (optional).</p>
          <div className="form-group">
            <textarea
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              rows={3}
              placeholder="Optional notes..."
              className="coaching-model-textarea"
            />
          </div>
        </section>

        <div className="form-actions">
          {!isEdit && (
            <>
              <Link to={tenantPath('/lists/providers')} className="btn-cancel">
                Cancel
              </Link>
              <button type="submit" disabled={loading} className="btn-save">
                {loading ? 'Saving...' : 'Create Provider'}
              </button>
            </>
          )}
        </div>
      </form>

    </div>
  );
}

export default ProviderForm;
